import { createContext, useContext, useEffect, useState } from "react";

import { getSessionContext, isNotFound, isUnavailable } from "../useLearning";

export const CONTEXT_STATUS = Object.freeze({
  NONE: "none", LOADING: "loading", READY: "ready", GONE: "gone", FORBIDDEN: "forbidden", NOT_CONNECTED: "not_connected",
  FAILED: "failed",
});

const GONE_REASON = "item_gone";
const FORBIDDEN_REASON = "not_your_session";
const HTTP_FORBIDDEN = 403;
const NONE = Object.freeze({ status: CONTEXT_STATUS.NONE, data: null });
const LOADING = Object.freeze({ status: CONTEXT_STATUS.LOADING, data: null });
const NOT_CONNECTED = Object.freeze({ status: CONTEXT_STATUS.NOT_CONNECTED, data: null });
const GONE = Object.freeze({ status: CONTEXT_STATUS.GONE, data: null });
const FORBIDDEN = Object.freeze({ status: CONTEXT_STATUS.FORBIDDEN, data: null });
const FAILED = Object.freeze({ status: CONTEXT_STATUS.FAILED, data: null });
const NO_ENTRIES = Object.freeze([]);

/**
 * The page's cache of loaded session contexts (a Map keyed on the session id), provided by the Learning Catalog page so both
 * views share it for the page's lifetime. Without a provider nothing is cached.
 */
export const SessionContextCache = createContext(null);

/** The session id a catalog or history item belongs to. Only `session_id`: a history event id may carry a suffix. */
export const sessionIdOf = (item) => (item && item.session_id) || "";

const readyOf = (data) => ({
  status: CONTEXT_STATUS.READY,
  data: { firstQuery: data.first_query || null, entries: Array.isArray(data.context) ? data.context : NO_ENTRIES,
    startedBy: data.started_by || "" },
});

// A 404 with the `item_gone` reason: the conversation is no longer there. A 403 `not_your_session`: it belongs to another
// engineer. Any other 404 (the endpoint is missing) or a 503 (the store is unreachable): not connected. Everything else is a
// failure the viewer can retry.
const failureOf = (error) => {
  if (isNotFound(error)) return error.message === GONE_REASON ? GONE : NOT_CONNECTED;
  if (error && error.status === HTTP_FORBIDDEN && error.message === FORBIDDEN_REASON) return FORBIDDEN;
  return isUnavailable(error) ? NOT_CONNECTED : FAILED;
};

const cachedOf = (cache, sessionId) => (sessionId && cache && cache.get(sessionId)) || null;

/**
 * How the conversation `sessionId` started (its first query and the context it was opened with), loaded when the caller mounts:
 * {status, data: {firstQuery, entries, startedBy} | null, retry}. A loaded answer (and a gone or forbidden one) is kept in the
 * page's cache, so opening the same conversation again shows it at once; a failure is not kept. The request is aborted when
 * the caller unmounts (the popup closes) or the session changes. No session id: NONE, nothing is fetched.
 */
export default function useSessionContext(sessionId) {
  const cache = useContext(SessionContextCache);
  const [round, setRound] = useState(0);
  const [state, setState] = useState(() => cachedOf(cache, sessionId) || (sessionId ? LOADING : NONE));

  useEffect(() => {
    if (!sessionId) { setState(NONE); return undefined; }
    const hit = cachedOf(cache, sessionId);
    if (hit) { setState(hit); return undefined; }
    const controller = new AbortController();
    const { signal } = controller;
    setState(LOADING);
    const settle = (next) => {
      if (signal.aborted) return;
      if (cache && next.status !== CONTEXT_STATUS.FAILED && next.status !== CONTEXT_STATUS.NOT_CONNECTED) cache.set(sessionId, next);
      setState(next);
    };
    getSessionContext(sessionId, { signal })
      .then((data) => settle(data ? readyOf(data) : NOT_CONNECTED))
      .catch((error) => settle(failureOf(error)));
    return () => controller.abort();
  }, [cache, sessionId, round]);

  return { ...state, retry: () => setRound((n) => n + 1) };
}
